import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumCurrency } from "./enums/enumCurrency.generated";

// Model Objects

export class AnnualProductionEntry extends ModelObject<AnnualProductionEntry> {

    /**
    * Estimate of Beer Production Figure over the next calendar year. Typically equal to last calendar year's actual production figure, unless good reason to believe otherwise.
    */
    public annualBeerProductionLitres: number = 0;
    private _annualEthanolProductionLitres: number | null = null;
    /**
    * Annual Ethanol Production figure for use in duty calculations. For UK this is the actual production in the previous year (year to 31st Jan), or the estimate of production for the current year if that is above the small production limit (450000 Litres in 2023)..
    */
    public get annualEthanolProductionLitres(): number | null { return this._annualEthanolProductionLitres }
    public set annualEthanolProductionLitres(value: number | null) {
      this._annualEthanolProductionLitres = value === undefined ? null : value;
    }
    /**
    * Date from which this production figure should be used for duty calculations.
    */
    public effectiveDate: LocalDate = LocalDate.ofEpochDay(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.annualBeerProductionLitres = jsonSerializedObject.annualBeerProductionLitres;
          this.annualEthanolProductionLitres = jsonSerializedObject.annualEthanolProductionLitres == null ? null : jsonSerializedObject.annualEthanolProductionLitres;
          this.effectiveDate = LocalDate.parse(jsonSerializedObject.effectiveDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.annualBeerProductionLitres = this.annualBeerProductionLitres;
        toRet.annualEthanolProductionLitres = this.annualEthanolProductionLitres == null ? null : this.annualEthanolProductionLitres;
        toRet.effectiveDate = this.effectiveDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class SupplierManufacturerReadOnly extends ModelObject<SupplierManufacturerReadOnly> {

    /**
    * Identifies if this Manufacturer is the tenant themselves.
    */
    public isTenant: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.isTenant = jsonSerializedObject.isTenant;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.isTenant = this.isTenant;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class SupplierManufacturerContactReadOnly extends ModelObject<SupplierManufacturerContactReadOnly> {

    /**
    * SupplierManufacturer Id
    */
    public supplierManufacturerId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.supplierManufacturerId = Guid.parseOrEmpty(jsonSerializedObject.supplierManufacturerId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.supplierManufacturerId = this.supplierManufacturerId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "supplierManufacturerId", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerContactReadOnly.SupplierManufacturerId cannot be empty"},
    ];
}


export class SupplierManufacturerContact extends ModelObject<SupplierManufacturerContact> {

    public id: Guid = Guid.createEmpty();
    /**
    * Salutation
    * Not Nullable
    * Max Length : 1000
    */
    public salutation: string = "";
    /**
    * Forename
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public forename: string = "";
    /**
    * Surname
    * Not Nullable
    * Max Length : 1000
    */
    public surname: string = "";
    /**
    * JobTitle
    * Not Nullable
    * Max Length : 1000
    */
    public jobTitle: string = "";
    /**
    * Email Address
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Telephone number
    * Not Nullable
    * Max Length : 1000
    */
    public phoneNumber: string = "";
    /**
    * Phone Extension
    * Not Nullable
    * Max Length : 1000
    */
    public phoneExtension: string = "";
    /**
    * Mobile Phone Number
    * Not Nullable
    * Max Length : 1000
    */
    public mobileNumber: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    public readOnly: SupplierManufacturerContactReadOnly = new SupplierManufacturerContactReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.salutation = jsonSerializedObject.salutation;
          this.forename = jsonSerializedObject.forename;
          this.surname = jsonSerializedObject.surname;
          this.jobTitle = jsonSerializedObject.jobTitle;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.phoneNumber = jsonSerializedObject.phoneNumber;
          this.phoneExtension = jsonSerializedObject.phoneExtension;
          this.mobileNumber = jsonSerializedObject.mobileNumber;
          this.notes = jsonSerializedObject.notes;
          this.readOnly = new SupplierManufacturerContactReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.salutation = this.salutation;
        toRet.forename = this.forename;
        toRet.surname = this.surname;
        toRet.jobTitle = this.jobTitle;
        toRet.emailAddress = this.emailAddress;
        toRet.phoneNumber = this.phoneNumber;
        toRet.phoneExtension = this.phoneExtension;
        toRet.mobileNumber = this.mobileNumber;
        toRet.notes = this.notes;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerContact.Id cannot be empty"},
            {field: "salutation", rule: (v: any) => v == null || v.length <= 1000 || "Salutation has too many characters (1000)"},
            {field: "forename", rule: (v: any) => v == null || v.length !== 0 || "Forename cannot be empty"},
            {field: "forename", rule: (v: any) => v == null || v.length <= 1000 || "Forename has too many characters (1000)"},
            {field: "surname", rule: (v: any) => v == null || v.length <= 1000 || "Surname has too many characters (1000)"},
            {field: "jobTitle", rule: (v: any) => v == null || v.length <= 1000 || "JobTitle has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "phoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "PhoneNumber has too many characters (1000)"},
            {field: "phoneExtension", rule: (v: any) => v == null || v.length <= 1000 || "PhoneExtension has too many characters (1000)"},
            {field: "mobileNumber", rule: (v: any) => v == null || v.length <= 1000 || "MobileNumber has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SupplierManufacturerAccreditationReadOnly extends ModelObject<SupplierManufacturerAccreditationReadOnly> {

    /**
    * SupplierManufacturer Id
    */
    public supplierManufacturerId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.supplierManufacturerId = Guid.parseOrEmpty(jsonSerializedObject.supplierManufacturerId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.supplierManufacturerId = this.supplierManufacturerId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "supplierManufacturerId", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerAccreditationReadOnly.SupplierManufacturerId cannot be empty"},
    ];
}


export class SupplierManufacturerAccreditation extends ModelObject<SupplierManufacturerAccreditation> {

    public id: Guid = Guid.createEmpty();
    /**
    * Description of the Accreditation
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public accreditation: string = "";
    private _expiryDate: LocalDate | null = null;
    /**
    * Date which this accreditation expires (if known).
    */
    public get expiryDate(): LocalDate | null { return this._expiryDate }
    public set expiryDate(value: LocalDate | null) {
      this._expiryDate = value === undefined ? null : value;
    }
    /**
    * Details
    * Not Nullable
    * Max Length : 10000
    */
    public details: string = "";
    public readOnly: SupplierManufacturerAccreditationReadOnly = new SupplierManufacturerAccreditationReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.accreditation = jsonSerializedObject.accreditation;
          this.expiryDate = jsonSerializedObject.expiryDate == null ? null : LocalDate.parse(jsonSerializedObject.expiryDate);
          this.details = jsonSerializedObject.details;
          this.readOnly = new SupplierManufacturerAccreditationReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.accreditation = this.accreditation;
        toRet.expiryDate = this.expiryDate == null ? null : this.expiryDate.toString();
        toRet.details = this.details;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "SupplierManufacturerAccreditation.Id cannot be empty"},
            {field: "accreditation", rule: (v: any) => v == null || v.length !== 0 || "Accreditation cannot be empty"},
            {field: "accreditation", rule: (v: any) => v == null || v.length <= 1000 || "Accreditation has too many characters (1000)"},
            {field: "details", rule: (v: any) => v == null || v.length <= 10000 || "Details has too many characters (10000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SupplierManufacturer extends ModelObjectWithDirtyCheck<SupplierManufacturer> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine1: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine2: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine3: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine4: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine5: string = "";
    /**
    * Address
    * Not Nullable
    * Max Length : 1000
    */
    public addressLine6: string = "";
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public businessName: string = "";
    /**
    * Code. Used for linking to external systems such as accounts package. Can not be changed after creation.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public code: string = "";
    /**
    * Contact Name
    * Not Nullable
    * Max Length : 1000
    */
    public contactName: string = "";
    /**
    * Email
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Is this a Manufacturer?
    */
    public isManufacturer: boolean = false;
    /**
    * Is this a Supplier?
    */
    public isSupplier: boolean = false;
    /**
    * Mobile Number
    * Not Nullable
    * Max Length : 1000
    */
    public mobileNumber: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Telephone No
    * Not Nullable
    * Max Length : 1000
    */
    public phoneNumber: string = "";
    /**
    * Post Code
    * Not Nullable
    * Max Length : 1000
    */
    public postcode: string = "";
    private _vatCodeId: Guid | null = null;
    /**
    * Standard Vat Code Id
    */
    public get vatCodeId(): Guid | null { return this._vatCodeId }
    public set vatCodeId(value: Guid | null) {
      this._vatCodeId = value === undefined ? null : value;
    }
    /**
    * VAT Registration Code
    * Not Nullable
    * Max Length : 1000
    */
    public vatReg: string = "";
    public annualProduction: (AnnualProductionEntry)[] = [];
    public readOnly: SupplierManufacturerReadOnly = new SupplierManufacturerReadOnly();
    public isHidden: boolean = false;
    /**
    * AWRS Number
    * Not Nullable
    * Max Length : 1000
    */
    public awrsNumber: string = "";
    /**
    * International Bank Account Number
    * Not Nullable
    * Max Length : 1000
    */
    public iban: string = "";
    /**
    * Bank Identification Code
    * Not Nullable
    * Max Length : 1000
    */
    public bic: string = "";
    /**
    * Default delivery price to add to purchase orders for this supplier.
    */
    public defaultPoDeliveryCharge: number = 0;
    private _purchaseOrderTemplateId: Guid | null = null;
    /**
    * Purchase order document template set to this supplier.
    */
    public get purchaseOrderTemplateId(): Guid | null { return this._purchaseOrderTemplateId }
    public set purchaseOrderTemplateId(value: Guid | null) {
      this._purchaseOrderTemplateId = value === undefined ? null : value;
    }
    public webAddress: string = "";
    /**
    * Identifies if this supplier is approved
    */
    public isApproved: boolean = false;
    private _lastApprovedDate: LocalDate | null = null;
    /**
    * Last approved date for this supplier
    */
    public get lastApprovedDate(): LocalDate | null { return this._lastApprovedDate }
    public set lastApprovedDate(value: LocalDate | null) {
      this._lastApprovedDate = value === undefined ? null : value;
    }
    private _defaultStockLocationId: Guid | null = null;
    /**
    * Default Stock Location
    */
    public get defaultStockLocationId(): Guid | null { return this._defaultStockLocationId }
    public set defaultStockLocationId(value: Guid | null) {
      this._defaultStockLocationId = value === undefined ? null : value;
    }
    /**
    * Company Number.
    * Not Nullable
    * Max Length : 1000
    */
    public companyRegNumber: string = "";
    /**
    * The ISO 4217 Currency Code.
    */
    public currencyCode: EnumCurrency = EnumCurrency.none;
    public contacts: (SupplierManufacturerContact)[] = [];
    public accreditations: (SupplierManufacturerAccreditation)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.addressLine1 = jsonSerializedObject.addressLine1;
          this.addressLine2 = jsonSerializedObject.addressLine2;
          this.addressLine3 = jsonSerializedObject.addressLine3;
          this.addressLine4 = jsonSerializedObject.addressLine4;
          this.addressLine5 = jsonSerializedObject.addressLine5;
          this.addressLine6 = jsonSerializedObject.addressLine6;
          this.businessName = jsonSerializedObject.businessName;
          this.code = jsonSerializedObject.code;
          this.contactName = jsonSerializedObject.contactName;
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.isManufacturer = jsonSerializedObject.isManufacturer;
          this.isSupplier = jsonSerializedObject.isSupplier;
          this.mobileNumber = jsonSerializedObject.mobileNumber;
          this.notes = jsonSerializedObject.notes;
          this.phoneNumber = jsonSerializedObject.phoneNumber;
          this.postcode = jsonSerializedObject.postcode;
          this.vatCodeId = jsonSerializedObject.vatCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
          this.vatReg = jsonSerializedObject.vatReg;
          this.annualProduction = jsonSerializedObject.annualProduction.map((v: any) => new AnnualProductionEntry(v));
          this.readOnly = new SupplierManufacturerReadOnly(jsonSerializedObject.readOnly);
          this.isHidden = jsonSerializedObject.isHidden;
          this.awrsNumber = jsonSerializedObject.awrsNumber;
          this.iban = jsonSerializedObject.iban;
          this.bic = jsonSerializedObject.bic;
          this.defaultPoDeliveryCharge = jsonSerializedObject.defaultPoDeliveryCharge;
          this.purchaseOrderTemplateId = jsonSerializedObject.purchaseOrderTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.purchaseOrderTemplateId);
          this.webAddress = jsonSerializedObject.webAddress;
          this.isApproved = jsonSerializedObject.isApproved;
          this.lastApprovedDate = jsonSerializedObject.lastApprovedDate == null ? null : LocalDate.parse(jsonSerializedObject.lastApprovedDate);
          this.defaultStockLocationId = jsonSerializedObject.defaultStockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultStockLocationId);
          this.companyRegNumber = jsonSerializedObject.companyRegNumber;
          this.currencyCode = EnumCurrency.getByValue(jsonSerializedObject.currencyCode, this.currencyCode);
          this.contacts = jsonSerializedObject.contacts.map((v: any) => new SupplierManufacturerContact(v));
          this.accreditations = jsonSerializedObject.accreditations.map((v: any) => new SupplierManufacturerAccreditation(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.addressLine1 = this.addressLine1;
        toRet.addressLine2 = this.addressLine2;
        toRet.addressLine3 = this.addressLine3;
        toRet.addressLine4 = this.addressLine4;
        toRet.addressLine5 = this.addressLine5;
        toRet.addressLine6 = this.addressLine6;
        toRet.businessName = this.businessName;
        toRet.code = this.code;
        toRet.contactName = this.contactName;
        toRet.emailAddress = this.emailAddress;
        toRet.isManufacturer = this.isManufacturer;
        toRet.isSupplier = this.isSupplier;
        toRet.mobileNumber = this.mobileNumber;
        toRet.notes = this.notes;
        toRet.phoneNumber = this.phoneNumber;
        toRet.postcode = this.postcode;
        toRet.vatCodeId = this.vatCodeId == null ? null : this.vatCodeId.toString();
        toRet.vatReg = this.vatReg;
        toRet.annualProduction = this.annualProduction.map(v => v.toJsonSerializedObject());
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.isHidden = this.isHidden;
        toRet.awrsNumber = this.awrsNumber;
        toRet.iban = this.iban;
        toRet.bic = this.bic;
        toRet.defaultPoDeliveryCharge = this.defaultPoDeliveryCharge;
        toRet.purchaseOrderTemplateId = this.purchaseOrderTemplateId == null ? null : this.purchaseOrderTemplateId.toString();
        toRet.webAddress = this.webAddress;
        toRet.isApproved = this.isApproved;
        toRet.lastApprovedDate = this.lastApprovedDate == null ? null : this.lastApprovedDate.toString();
        toRet.defaultStockLocationId = this.defaultStockLocationId == null ? null : this.defaultStockLocationId.toString();
        toRet.companyRegNumber = this.companyRegNumber;
        toRet.currencyCode = this.currencyCode.value;
        toRet.contacts = this.contacts.map(v => v.toJsonSerializedObject());
        toRet.accreditations = this.accreditations.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "SupplierManufacturer.Id cannot be empty"},
            {field: "addressLine1", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine1 has too many characters (1000)"},
            {field: "addressLine2", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine2 has too many characters (1000)"},
            {field: "addressLine3", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine3 has too many characters (1000)"},
            {field: "addressLine4", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine4 has too many characters (1000)"},
            {field: "addressLine5", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine5 has too many characters (1000)"},
            {field: "addressLine6", rule: (v: any) => v == null || v.length <= 1000 || "AddressLine6 has too many characters (1000)"},
            {field: "businessName", rule: (v: any) => v == null || v.length !== 0 || "BusinessName cannot be empty"},
            {field: "businessName", rule: (v: any) => v == null || v.length <= 1000 || "BusinessName has too many characters (1000)"},
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "contactName", rule: (v: any) => v == null || v.length <= 1000 || "ContactName has too many characters (1000)"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "mobileNumber", rule: (v: any) => v == null || v.length <= 1000 || "MobileNumber has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "phoneNumber", rule: (v: any) => v == null || v.length <= 1000 || "PhoneNumber has too many characters (1000)"},
            {field: "postcode", rule: (v: any) => v == null || v.length <= 1000 || "Postcode has too many characters (1000)"},
            {field: "vatCodeId", rule: (v: any) => v == null || !v.isEmpty() || "SupplierManufacturer.VatCodeId cannot be empty"},
            {field: "vatReg", rule: (v: any) => v == null || v.length <= 1000 || "VatReg has too many characters (1000)"},
            {field: "annualProduction", rule: (v: any) => !!v || "annualProduction is required"},
            {field: "annualProduction", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AnnualProductionEntry[]).reduce((acc: Array<string>, curr: AnnualProductionEntry) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "awrsNumber", rule: (v: any) => v == null || v.length <= 1000 || "AwrsNumber has too many characters (1000)"},
            {field: "iban", rule: (v: any) => v == null || v.length <= 1000 || "Iban has too many characters (1000)"},
            {field: "bic", rule: (v: any) => v == null || v.length <= 1000 || "Bic has too many characters (1000)"},
            {field: "purchaseOrderTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "SupplierManufacturer.PurchaseOrderTemplateId cannot be empty"},
            {field: "webAddress", rule: (v: any) => v == null || v.length <= 1000 || "WebAddress has too many characters (1000)"},
            {field: "defaultStockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "SupplierManufacturer.DefaultStockLocationId cannot be empty"},
            {field: "companyRegNumber", rule: (v: any) => v == null || v.length <= 1000 || "CompanyRegNumber has too many characters (1000)"},
            {field: "contacts", rule: (v: any) => !!v || "contacts is required"},
            {field: "contacts", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SupplierManufacturerContact[]).reduce((acc: Array<string>, curr: SupplierManufacturerContact) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "accreditations", rule: (v: any) => !!v || "accreditations is required"},
            {field: "accreditations", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SupplierManufacturerAccreditation[]).reduce((acc: Array<string>, curr: SupplierManufacturerAccreditation) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SupplierManufacturerService extends ServiceBase {

  public async getAllSupplierManufacturers(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SupplierManufacturer)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.supplierManufacturers.map((v: any) => new SupplierManufacturer(v));;
    return this.makeJsonRequest("supplierManufacturer/v1/getAllSupplierManufacturers", req, false, extractor, opts);
  };

  public async getSupplierManufacturer(supplierManufacturerId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SupplierManufacturer> {
    const req = {
    tenantId:api.tenantId.toString(),
    supplierManufacturerId:supplierManufacturerId.toString(),
    };
    const extractor = (response:any) => new SupplierManufacturer(response.supplierManufacturer);;
    return this.makeJsonRequest("supplierManufacturer/v1/getSupplierManufacturer", req, false, extractor, opts);
  };

  public async getSupplierManufacturers(supplierManufacturerIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SupplierManufacturer)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    supplierManufacturerIds:supplierManufacturerIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.supplierManufacturers.map((v: any) => new SupplierManufacturer(v));;
    return this.makeJsonRequest("supplierManufacturer/v1/getSupplierManufacturers", req, false, extractor, opts);
  };

  public async saveSupplierManufacturer(supplierManufacturer: SupplierManufacturer, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    supplierManufacturer:supplierManufacturer.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("supplierManufacturer/v1/saveSupplierManufacturer", req, true, extractor, opts);
  };

  public async setSupplierManufacturerIsHidden(supplierManufacturerId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    supplierManufacturerId:supplierManufacturerId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("supplierManufacturer/v1/setSupplierManufacturerIsHidden", req, true, extractor, opts);
  };

  public async generateSupplierManufacturerCode(supplierManufacturerName: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:api.tenantId.toString(),
    supplierManufacturerName:supplierManufacturerName,
    };
    const extractor = (response:any) => response.code;;
    return this.makeJsonRequest("supplierManufacturer/v1/generateSupplierManufacturerCode", req, false, extractor, opts);
  };
}

export const supplierManufacturerService = new SupplierManufacturerService();